/**
 * Error Boundary Component
 * React error boundary with recovery options
 */

import React, { Component } from 'react';
import { logError } from '../services/errorLoggingService';

// ============================================================================
// ERROR BOUNDARY CLASS
// ============================================================================

class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            retryCount: 0
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ errorInfo });

        // Log the error
        logError(error, {
            componentStack: errorInfo?.componentStack,
            component: this.props.name || 'Unknown'
        });
    }

    handleRetry = () => {
        this.setState(prev => ({
            hasError: false,
            error: null,
            errorInfo: null,
            retryCount: prev.retryCount + 1
        }));
    };

    handleReload = () => {
        window.location.reload();
    };

    handleGoHome = () => {
        window.location.href = '/';
    };

    render() {
        if (this.state.hasError) {
            // Custom fallback UI
            if (this.props.fallback) {
                return this.props.fallback({
                    error: this.state.error,
                    retry: this.handleRetry,
                    reload: this.handleReload
                });
            }

            // Default error UI
            return (
                <div style={styles.container}>
                    <div style={styles.card}>
                        <div style={styles.icon}>⚠️</div>
                        <h2 style={styles.title}>Something went wrong</h2>
                        <p style={styles.message}>
                            {this.state.error?.userMessage ||
                                'An unexpected error occurred. Please try again.'}
                        </p>

                        <div style={styles.buttons}>
                            {this.state.retryCount < 3 && (
                                <button
                                    onClick={this.handleRetry}
                                    style={styles.primaryButton}
                                >
                                    Try Again
                                </button>
                            )}

                            <button
                                onClick={this.handleReload}
                                style={styles.secondaryButton}
                            >
                                Refresh Page
                            </button>

                            <button
                                onClick={this.handleGoHome}
                                style={styles.linkButton}
                            >
                                Go to Home
                            </button>
                        </div>

                        {process.env.NODE_ENV === 'development' && (
                            <details style={styles.details}>
                                <summary>Error Details</summary>
                                <pre style={styles.stack}>
                                    {this.state.error?.stack}
                                    {'\n\nComponent Stack:\n'}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

// ============================================================================
// STYLES
// ============================================================================

const styles = {
    container: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
    },
    card: {
        background: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        borderRadius: '16px',
        padding: '40px',
        maxWidth: '400px',
        width: '100%',
        textAlign: 'center',
        border: '1px solid rgba(255, 255, 255, 0.1)'
    },
    icon: {
        fontSize: '48px',
        marginBottom: '16px'
    },
    title: {
        color: '#fff',
        fontSize: '24px',
        fontWeight: '600',
        marginBottom: '12px'
    },
    message: {
        color: 'rgba(255, 255, 255, 0.7)',
        fontSize: '14px',
        lineHeight: '1.5',
        marginBottom: '24px'
    },
    buttons: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
    },
    primaryButton: {
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: '#fff',
        border: 'none',
        borderRadius: '8px',
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'transform 0.2s'
    },
    secondaryButton: {
        background: 'rgba(255, 255, 255, 0.1)',
        color: '#fff',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '8px',
        padding: '12px 24px',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer'
    },
    linkButton: {
        background: 'transparent',
        color: 'rgba(255, 255, 255, 0.6)',
        border: 'none',
        padding: '8px',
        fontSize: '13px',
        cursor: 'pointer',
        textDecoration: 'underline'
    },
    details: {
        marginTop: '24px',
        textAlign: 'left',
        color: 'rgba(255, 255, 255, 0.5)',
        fontSize: '12px'
    },
    stack: {
        marginTop: '12px',
        padding: '12px',
        background: 'rgba(0, 0, 0, 0.3)',
        borderRadius: '8px',
        overflow: 'auto',
        maxHeight: '200px',
        fontSize: '10px',
        fontFamily: 'monospace',
        whiteSpace: 'pre-wrap'
    }
};

// ============================================================================
// WRAPPER HOOK (for functional components)
// ============================================================================

export const useErrorBoundary = () => {
    const [error, setError] = React.useState(null);

    const resetError = () => setError(null);

    const captureError = (err) => {
        setError(err);
        logError(err, { source: 'useErrorBoundary' });
    };

    return { error, resetError, captureError };
};

// ============================================================================
// EXPORTS
// ============================================================================

export default ErrorBoundary;
